import algoliasearch from "algoliasearch/lite";
import config from "../../config";

/**
 * WebMCP (https://webmachinelearning.github.io/webmcp/): tools an AI agent
 * in the browser can call on this site, to search the knowledge base and
 * read a page as Markdown. Browsers without WebMCP skip this.
 */

const MAX_RESULTS = 20;

const siteUrl = () =>
    window.location.origin +
    (config.gatsby.pathPrefix !== "/" ? config.gatsby.pathPrefix : "");

// Same mapping as markdownPath() in gatsby-node.js.
const markdownPath = slug => (slug === "/" ? "/index.md" : `${slug}.md`);

const search = async ({ query, limit = 5 }) => {
    const { algoliaAppId, algoliaSearchKey, indexName } = config.header.search;
    const index = algoliasearch(algoliaAppId, algoliaSearchKey).initIndex(indexName);
    const { hits } = await index.search(String(query), {
        hitsPerPage: Math.min(Math.max(Number(limit) || 5, 1), MAX_RESULTS),
        attributesToRetrieve: ["slug", "title", "metaTitle", "metaDescription", "platform"],
        attributesToHighlight: []
    });
    return hits.map(hit => ({
        title: hit.metaTitle || hit.title,
        description: hit.metaDescription || "",
        platform: hit.platform || "",
        url: siteUrl() + hit.slug,
        markdownUrl: siteUrl() + markdownPath(hit.slug)
    }));
};

const getPage = async ({ url }) => {
    const target = new URL(String(url), window.location.href);
    if (target.origin !== window.location.origin) {
        throw new Error("Only pages on this site can be read.");
    }
    const slug = target.pathname.replace(/\.md$/, "").replace(/\/+$/, "") || "/";
    const response = await fetch(markdownPath(slug === "/index" ? "/" : slug));
    if (!response.ok) throw new Error(`No page found at ${target.pathname}.`);
    return response.text();
};

const TOOLS = [
    {
        name: "search_knowledge_base",
        description:
            "Search the Evinced accessibility knowledge base: web and mobile accessibility issues, UI patterns, WCAG, EN 301 549 and Section 508 criteria, and testing guides. Returns matching pages with their URLs and Markdown URLs.",
        inputSchema: {
            type: "object",
            properties: {
                query: {
                    type: "string",
                    description: "What to search for, such as an issue name, a WCAG criterion number or a UI pattern."
                },
                limit: {
                    type: "integer",
                    minimum: 1,
                    maximum: MAX_RESULTS,
                    description: "How many results to return (default 5)."
                }
            },
            required: ["query"]
        },
        execute: search
    },
    {
        name: "get_page_markdown",
        description:
            "Get the full content of a knowledge base page as Markdown, given its URL or path (for example /web/web-validations/accessible-name).",
        inputSchema: {
            type: "object",
            properties: {
                url: { type: "string", description: "The page's URL or path on this site." }
            },
            required: ["url"]
        },
        execute: getPage
    }
];

export const registerWebMcpTools = () => {
    const modelContext =
        (typeof document !== "undefined" && document.modelContext) ||
        (typeof navigator !== "undefined" && navigator.modelContext);
    if (!modelContext || typeof modelContext.registerTool !== "function") return;
    for (const tool of TOOLS) {
        Promise.resolve(modelContext.registerTool(tool)).catch(error => {
            console.warn(`[webmcp] couldn't register ${tool.name}:`, error);
        });
    }
};
